import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { AuthLayout } from '../components/auth/AuthLayout';
import { Banner, Button, Field, Input, buttonClasses } from '../components/ui';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token || !userId) {
    return (
      <AuthLayout title="Neues Passwort setzen">
        <Banner tone="danger" title="Ungültiger Link">
          Der Reset-Link ist ungültig oder unvollständig.
        </Banner>
        <Link to="/forgot-password" className={buttonClasses('primary', 'md', 'mt-4 w-full')}>
          Neuen Link anfordern
        </Link>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/user/reset-password', { userId, token, newPassword });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Setzen des Passworts. Der Link ist möglicherweise abgelaufen.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout title="Neues Passwort setzen">
        <Banner tone="success" title="Passwort erfolgreich gesetzt!">
          Du kannst dich jetzt mit deinem neuen Passwort anmelden.
        </Banner>
        <Link to="/login" className={buttonClasses('primary', 'md', 'mt-4 w-full')}>
          Zum Login
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Neues Passwort setzen"
      description="Wähle ein neues Passwort für dein Konto."
      footer={
        <Link to="/login" className="font-semibold text-brand hover:underline">
          Zurück zum Login
        </Link>
      }
    >
      {error && (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field label="Neues Passwort" htmlFor="newPassword">
          <Input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Mindestens 8 Zeichen"
            required
            disabled={isLoading}
            minLength={8}
            autoComplete="new-password"
          />
        </Field>

        <Field label="Passwort bestätigen" htmlFor="confirmPassword">
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Passwort wiederholen"
            required
            disabled={isLoading}
            minLength={8}
            autoComplete="new-password"
          />
        </Field>

        <Button type="submit" fullWidth loading={isLoading} disabled={isLoading || !newPassword || !confirmPassword}>
          Passwort speichern
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
